import { useRef } from 'react';
import { useOutlet, useLocation } from 'react-router-dom';
import { LayoutProvider, useLayout } from './LayoutContext';
import { DesktopNavRail, NAV_RAIL_COLLAPSED, NAV_RAIL_EXPANDED, NAV_FLOAT_GAP } from './DesktopNavRail';
import { MobileBottomNav } from './MobileBottomNav';
import { SpatialOverlay } from './SpatialOverlay';
import { MessagingLayout } from '@/components/drawer';
import { ViewAsOverlay } from '@/components/ImpersonationBanner';
import { useCrossTabAuth, useAuthStore } from '@/features/auth';
import { StickyChatWidget } from '@/features/messaging';
import { useGlobalNotifications } from '@/features/messaging/hooks';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { NotificationGate } from '@/components/NotificationGate';
import { AppVersionGate } from '@/components/AppVersionGate';
import { TermsAcceptanceModal } from '@/features/auth/components/TermsAcceptanceModal';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import { platform } from '@/utils/platform';

function AppLayoutInner() {
  const { sidebarCollapsed, sidebarSide } = useLayout();
  const isMobile = useMediaQuery(MD_BREAKPOINT);
  const shellRef = useRef<HTMLDivElement>(null);
  const isImpersonating = useAuthStore((s) => s.isImpersonating);

  // Activate cross-tab auth sync within the authenticated layout
  useCrossTabAuth();

  // Global notification WebSocket — instant conversation list updates
  useGlobalNotifications();

  // FCM push notifications — web + native (mandatory)
  const { pushBlocked, enableNotifications, checkPushGranted } = usePushNotifications();

  const outlet = useOutlet();
  const { pathname } = useLocation();
  // Only trigger spatial overlay for chat sub-routes (/messages/:id, /messages/new, etc.)
  // The messages list (/messages) renders as a regular page with bottom nav visible.
  // Strip trailing slash — react-router can resolve /messages/* to /messages/ when
  // the splat is empty, which would incorrectly match as a chat sub-route.
  const normalizedPath = pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : pathname;
  const isInChat = normalizedPath !== '/messages' && normalizedPath.startsWith('/messages/');

  // Cache last non-chat outlet so the shell shows the previous tab
  // content during the spatial scale-down animation
  const cachedTabOutlet = useRef<React.ReactNode>(null);
  if (!isInChat && outlet) {
    cachedTabOutlet.current = outlet;
  }

  // On mobile + in chat: show cached previous tab (parent card content).
  // If cache is null (e.g. deep-link/notification from another tab), show
  // a minimal gradient placeholder so the swipe-back parent card isn't blank.
  const mainContent =
    isMobile && isInChat
      ? cachedTabOutlet.current ?? (
          <div className="flex h-full min-h-dvh items-center justify-center">
            <div className="h-32 w-32 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 opacity-50" />
          </div>
        )
      : outlet;

  // Desktop sidebar is flush (no float gap), so content starts right after it
  const sidebarWidth = isMobile ? 0 : sidebarCollapsed ? NAV_RAIL_COLLAPSED : NAV_RAIL_EXPANDED;
  const contentLeft = isMobile ? 0 : sidebarWidth;

  // Content top offset on mobile:
  // Android: fitsSystemWindows="true" in activity_main.xml pushes WebView below
  //   the status bar natively, so only add the float gap here.
  // iOS: env(safe-area-inset-top) handles the notch/Dynamic Island.
  const contentTop = isMobile
    ? platform.isAndroid
      ? `${NAV_FLOAT_GAP}px`
      : `calc(env(safe-area-inset-top, 0px) + ${NAV_FLOAT_GAP}px)`
    : '0px';

  return (
    <>
      <div ref={shellRef} className={platform.isNative ? 'h-dvh overflow-y-auto' : 'min-h-dvh'}>
        {/* Fixed NavRail — always physical left, hidden on mobile */}
        <DesktopNavRail />

        {/* Immersive impersonation overlay — renders nothing when not active */}
        <ViewAsOverlay />

        {/* Main content */}
        <main
          className="flex-1 p-4 sm:p-6 md:py-6 md:px-8"
          style={{
            backgroundColor: 'var(--user-page-bg)',
            color: 'var(--user-page-text)',
            '--page-card-bg': 'var(--user-page-card-bg)',
            '--page-card-border': 'var(--user-page-card-border)',
            ...(sidebarSide === 'right' ? { marginRight: contentLeft } : { marginLeft: contentLeft }),
            marginTop: `calc(${contentTop} + var(--view-as-offset, 0px))`,
            paddingBottom: isMobile ? (platform.isAndroid ? '4rem' : 'calc(4rem + env(safe-area-inset-bottom, 0px))') : undefined,
            transition: 'margin-left 0.3s ease-in-out, margin-top 0.5s ease-in-out, filter 0.3s ease-in-out',
            filter: isImpersonating ? 'brightness(0.98) saturate(0.9)' : undefined,
          } as React.CSSProperties & Record<string, string>}
        >
          {mainContent}
        </main>

        <MobileBottomNav />
      </div>

      {/* Sticky chat widget — disabled for now */}
      {/* {!isMobile && !pathname.startsWith('/studio') && <StickyChatWidget />} */}

      {/* Spatial overlay — mobile only, siblings of shell so unaffected by transform */}
      {isMobile && (
        <SpatialOverlay isActive={isInChat} shellRef={shellRef}>
          <MessagingLayout />
        </SpatialOverlay>
      )}

      {/* Terms acceptance gate — blocks app until terms are accepted */}
      <TermsAcceptanceModal />

      {/* Full-screen notification gate — blocks app until push is granted */}
      <NotificationGate
        blocked={pushBlocked}
        onEnable={enableNotifications}
        onCheck={checkPushGranted}
      />

      {/* Android browser → install native app; outdated native → force update */}
      <AppVersionGate />
    </>
  );
}

export function AppLayout() {
  return (
    <LayoutProvider>
      <AppLayoutInner />
    </LayoutProvider>
  );
}
